import {useInjection} from "inversify-react";
import {useCallback, useEffect, useState} from "react";
import ApiService from "../../services/apiService";
import {DiscountCodeApi, DiscountCodeBulkDeleteDto, DiscountCodeDto} from "../../api";
import DiscountCodesList from "./discountCodeList";
import styles from "./discountCodeListContainer.module.less";
import {useNavigate} from "react-router-dom";
import {RoutingPaths} from "../routing/routingContainer";
import {Button} from "antd";
import {discountTypeTranslation} from "../../helpers/DiscountTypeHelper";
import {AsyncButtonPopconfirm} from "digimuth-components/dist";

export default function DiscountCodesListContainer() {
    const apiService = useInjection(ApiService);
    const [discountCodes, setDiscountCodes] = useState<DiscountCodeDto[] | null>();
    const [selectedDiscountCodeIds, setSelectedDiscountCodeIds] = useState<number[]>([]);
    const navigate = useNavigate();

    const refreshDiscountCodesList = useCallback(async () => {
        try {
            const discountCodes = await apiService.getApi(DiscountCodeApi).apiDiscountCodeGet();
            setDiscountCodes(discountCodes.data);
        } catch (e: any) {
            console.log(e.response.data);
        }
    }, [apiService]);

    const onAddClicked = () => {
        navigate(RoutingPaths.discountCodeAdd.route);
    }

    const onEditClicked = (id: number) => {
        navigate(RoutingPaths.discountCodeEdit.route.replace(":id", id.toString()));
    }

    const onDeleteClicked = async (id: number) => {
        const response = await apiService.getApi(DiscountCodeApi).apiDiscountCodeIdDelete(id)

        if (response.request.status !== 200) {
            console.log("Error occured while deleting data"); //add any warnings?
        } else {
            setSelectedDiscountCodeIds(currentValue => currentValue.filter(currentId => currentId !== id));
            refreshDiscountCodesList();
        }
    }

    const onDeleteSelectedClicked = async () => {
        await apiService.getApi(DiscountCodeApi).apiDiscountCodeBulkDeletePost({
            ids: selectedDiscountCodeIds
        } as DiscountCodeBulkDeleteDto);
        setSelectedDiscountCodeIds([]);
        await refreshDiscountCodesList();
    };

    const escapeCsvValue = (value: string | number | boolean | null | undefined) => {
        if (value === null || value === undefined) {
            return "";
        }

        const normalizedValue = value.toString().replace(/"/g, "\"\"");
        return `"${normalizedValue}"`;
    };

    const formatDate = (date?: string | null) => {
        if (!date) {
            return "";
        }

        return new Date(date).toLocaleString("pl-PL");
    };

    const onExportClicked = () => {
        if (!discountCodes?.length) {
            return;
        }

        const headers = [
            "Kod",
            "Data ważności",
            "Typ obniżki",
            "Wartość obniżki",
            "Minimalna kwota zamówienia",
            "Limit użyć",
            "Minimalna kwota tylko dla produktów objętych kodem",
            "ID produktów",
            "ID kategorii"
        ];

        const rows = discountCodes.map(code => [
            code.code,
            formatDate(code.expirationDate),
            discountTypeTranslation(code.discountType!)?.translation ?? "",
            code.discountValue,
            code.minimumOrderAmount,
            code.usageLimit ?? "Bez limitu",
            code.minimumOrderAmountOnlyForApplicableProducts ? "Tak" : "Nie",
            code.applicableProductIds?.join(", ") ?? "",
            code.applicableCategoryIds?.join(", ") ?? ""
        ]);

        const csvContent = [headers, ...rows]
            .map(row => row.map(value => escapeCsvValue(value)).join(";"))
            .join("\n");

        const blob = new Blob(["\uFEFF" + csvContent], {type: "text/csv;charset=utf-8;"});
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `kody-rabatowe-${new Date().toISOString().slice(0, 10)}.csv`;
        link.click();
        window.URL.revokeObjectURL(url);
    };

    useEffect(() => {
        refreshDiscountCodesList();
    }, [refreshDiscountCodesList]);

    return <div className={styles.content}>
        <DiscountCodesList data={discountCodes || null}
                           onAddClicked={onAddClicked}
                           onEditClicked={onEditClicked}
                           onDeleteClicked={onDeleteClicked}
                           tableProps={{
                               rowSelection: {
                                   selectedRowKeys: selectedDiscountCodeIds,
                                   onChange: (selectedRowKeys) => setSelectedDiscountCodeIds(selectedRowKeys as number[])
                               }
                           }}
                           customActions={() => (
                               <>
                                   <Button onClick={onExportClicked} disabled={!discountCodes?.length}>
                                       Eksportuj CSV
                                   </Button>
                                   <AsyncButtonPopconfirm
                                       danger
                                       title={`Czy na pewno chcesz usunąć zaznaczone kody (${selectedDiscountCodeIds.length})?`}
                                       onConfirm={onDeleteSelectedClicked}
                                       okText="Ok"
                                       cancelText="Anuluj"
                                       disabled={!selectedDiscountCodeIds.length}
                                   >
                                       Usuń zaznaczone
                                   </AsyncButtonPopconfirm>
                               </>
                           )}
        />
    </div>
}
